/**
 * Microsoft Clarity — session recordings + heatmaps for the WHOLE app.
 *
 * Design constraints (REQUIREMENTS/API/plan/2026-09-19_plan_01-microsoft-clarity-ganze-app-maskiert.md):
 * - **Consent first**: nothing loads — not even Clarity's cookieless mode —
 *   until the user accepts the analytics cookie category (`ClarityAnalytics`).
 * - **Production only**: gated on `CLARITY_PROJECT_ID` from the runtime config,
 *   which only the prod container injects — so Clarity never runs in dev/staging.
 * - **Masked**: `index.html` puts `data-clarity-mask="True"` on `<body>`, so every
 *   rendered text — portals included — is masked regardless of the masking
 *   mode in the dashboard.
 * - **Single entry point**: this is the only module that imports
 *   `@microsoft/clarity` (guarded by `clarity-grenze.test.ts`). The package's
 *   `consentV2()` without arguments grants EVERYTHING, and its methods throw
 *   when `init` has not run — nobody else should hold it.
 *
 * All functions are safe no-ops when there is no project id or no `window`.
 */

import Clarity from '@microsoft/clarity'
import { clearCookiesMatching } from './clearCookies'

declare global {
  interface Window {
    /** Clarity's command function: a queue stub (`q`) until the tracker loads. */
    clarity?: ((...args: unknown[]) => void) & { q?: ArrayLike<unknown>[] }
  }
}

/** off = never loaded · running = recording · stopped = torn down after a withdrawal */
let state: 'off' | 'running' | 'stopped' = 'off'

/** The Clarity project id from the runtime config, or null when Clarity is off. */
export function getClarityProjectId(): string | null {
  if (typeof window === 'undefined') return null
  const id = window.__RUNTIME_CONFIG__?.CLARITY_PROJECT_ID?.trim()
  return id ? id : null
}

/**
 * Load Clarity (first grant) or revive it (grant after a withdrawal), then
 * grant analytics storage. Returns true if Clarity is now recording, false if
 * it's disabled (no id / no window). Idempotent.
 */
export function enableClarity(): boolean {
  if (typeof window === 'undefined') return false
  const id = getClarityProjectId()
  if (!id) return false
  if (state === 'running') return true

  if (state === 'off') {
    Clarity.init(id)
  } else {
    // After `stop` Clarity leaves a stub that only wakes up on `start`; the
    // restart reuses the config of the first start (project id, upload url).
    window.clarity?.('start')
  }
  // Explicit values — the package default would grant ad storage as well. We
  // run no ads, and a denied ad_Storage also keeps Clarity from syncing the
  // MUID advertising cookie.
  Clarity.consentV2({ ad_Storage: 'denied', analytics_Storage: 'granted' })
  state = 'running'
  return true
}

/** Stop recording on consent withdrawal and remove Clarity's first-party storage. */
export function disableClarity(): void {
  if (typeof window === 'undefined' || state !== 'running') return
  // `stop`, NOT `consentv2` denied: Clarity 0.8.x answers a denial by
  // restarting itself 250 ms later in cookieless mode — it would keep
  // recording. `stop` tears every module down and leaves a stub that only
  // `start` revives (see enableClarity).
  window.clarity?.('stop')
  // ⚪ A withdrawal in the few ms before the tracker has loaded is replayed
  // after its start, so a `_clck` set in between survives until the next grant
  // or expiry. Nothing is recorded — the queued `stop` still wins.
  clearCookiesMatching(/^_cl(ck|sk)$/)
  try {
    window.sessionStorage.removeItem('_cltk')
  } catch {
    // Storage blocked (private mode, sandbox) — then there is nothing to remove.
  }
  state = 'stopped'
}

/** Test-only: reset the module's state between cases. */
export function __resetClarityForTests(): void {
  state = 'off'
}
